import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import Header from '../../components/ListViewHeader/Header';
import {APIErrorInterface, ReduxState} from '../../store/types';
import * as actions from '../../store/actions';
import {PhoneDirectory} from '../../store/types/Devices';
import {PaginationMode} from '../../components/DataGrid/types';
import DataGrid from '../../components/DataGrid/DataGrid';
import {addId, generateColumns, useStyles} from './DevicePhoneBookList.utils';
import usePageTitle from '../../hooks/usePageTitle';
import PermissionPlaceholder from "../../components/PermissionProvider/PermissionPlaceholder";
import {Permission} from "../../store/types/Permission";
import AlertDialog from '../../components/AlertDialog/AlertDialog';
import {DialogButton} from '../../components/AlertDialog/DialogContainer';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import {showErrorToast, showToast} from "../../utils/showToast";
import {YesNo} from '../../store/types/CallScreening';
import NewDevicePhoneBookDialog from './NewDevicePhoneBookDialog';

const DevicePhoneBookList = () => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const classes = useStyles();

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    usePageTitle();

    const i_customer: number = useSelector((state: ReduxState) => state.generic?.sessionData?.i_customer ?? 0);

    const [showAddNewDevicePhoneBookDialog, setShowAddNewDevicePhoneBookDialog] = useState(false);
    const handleAddNewDevicePhoneBookClick = () => {
        setShowAddNewDevicePhoneBookDialog((prev) => !prev);
    };
    
    const [deleteDialog, setDeleteDialog] = useState<{
        isOpen: boolean;
        i_ua_config_directory?: number;
        name?: string;
    }>({
        isOpen: false
    });

    const handleDeleteAction = useCallback(() => {
        dispatch(actions.deleteDevicePhoneBook.request({
            i_ua_config_directory: deleteDialog.i_ua_config_directory || 0,
            callback: () => {
                closeDeleteDialog();
                fetchData({pageSize: pageSize, page: 0});
                showToast(t<string>('screens:devicePhoneBooks.devicePhoneBookDeleted'));
            }, 
            errorCallback: (err: APIErrorInterface) => {
                closeDeleteDialog();
                fetchData({pageSize: pageSize, page: 0});
                showErrorToast(t<string>('errors:devicePhoneBooks.devicePhoneBookCantBeDeleted', {
                    phoneBookName: deleteDialog.name ?? ''
                }));
            },
        }));
    }, [pageSize, deleteDialog]);
    
    const closeDeleteDialog = () => {
        setDeleteDialog({
            isOpen: false,
            i_ua_config_directory: 0,
            name: deleteDialog?.name
        });
        setTimeout(() => {
            setDeleteDialog({
                isOpen: false,
                i_ua_config_directory: undefined,
                name: undefined
            });
        }, 200);
    };

    const {
        phoneDirectories = [],
        isLoadingPhoneDirectories = false,
        totalPhoneDirectories,
        createdPhoneDirectoryId,
        isDeletingPhoneBook = false,
    } = useSelector((state: ReduxState) => state.devices);

    const timezoneOffset = useSelector<ReduxState, number>(
        (state) => state.generic.sessionData?.tz_offset || 0,
    );

    useEffect(() => {
        if (!showAddNewDevicePhoneBookDialog && createdPhoneDirectoryId) {
            fetchData();
        }
    }, [showAddNewDevicePhoneBookDialog, createdPhoneDirectoryId]);

    const fetchData = (params?: { pageSize: number; page: number }) => {
        const _pageSize = params?.pageSize ?? pageSize ?? 100;
        const _page = params?.page ?? page ?? 0;
        dispatch(
            actions.getDevicePhoneBookList.request({
                get_total: 1,
                get_total_records: YesNo.Yes,
                i_customer: i_customer,
                limit: _pageSize,
                offset: _page * _pageSize,
            })
        );
    };

    useEffect(() => {
        fetchData();
    }, []);
    
    const handleDelete = (obj: PhoneDirectory) => {
        setDeleteDialog({isOpen: true, i_ua_config_directory: obj.i_ua_config_directory, name: obj.name});
    };

    const columns = useMemo(() => generateColumns(t, 
        handleDelete, 
        classes),
    [timezoneOffset]);

    return (
        <PermissionPlaceholder permission={Permission.Inventory.DevicePhoneBooks.value}>
            <div className={classes.mainContainer}>
                <div className={classes.scrollable}>
                    <div className={classes.content}>
                        <PermissionProvider
                            permission={Permission.Inventory.DevicePhoneBooks.value}
                        >
                            <Header
                                title={t('screens:devicePhoneBooks.listTitle')}
                                totalNumber={totalPhoneDirectories || 0}
                                dataQa={'device-phone-book-list-view-header'}
                                customClass={classes.header}
                                plusIcon={true}
                                buttonText={t('common:addNewButton')}
                                buttonOnClick={handleAddNewDevicePhoneBookClick}
                                showDetails={false}
                            >
                            </Header>
                        </PermissionProvider>

                        <div style={{marginTop: 16}}></div>

                        <DataGrid<PhoneDirectory>
                            columns={columns}
                            data={phoneDirectories.map(addId)}
                            className={classes.table}
                            rowCount={totalPhoneDirectories ?? 200000}
                            loading={isLoadingPhoneDirectories}
                            onPageChange={(params) => {
                                setPage(params.page);
                                fetchData({
                                    pageSize: params.pageSize,
                                    page: params.page,
                                });
                            }}
                            onPageSizeChange={(params) => {
                                setPageSize(params.pageSize);
                                fetchData({pageSize: params.pageSize, page: 0});
                            }}
                            paginationMode={PaginationMode.Server}
                            centeredRows
                            narrowRows
                            initialPageSize={10}
                            forceFirstPage={false}
                        />
                    </div>
                </div>

                <AlertDialog
                    className={classes.deleteDialog}
                    contentClass={classes.contentDialog}
                    isOpen={deleteDialog.isOpen}
                    dataQa={'delete-profile-dialog'}
                    hideHeader
                    description={t('screens:devicePhoneBooks.deleteRule', {
                        name: deleteDialog.name || '',
                    })}
                    dialogActionsButtons={[
                        <DialogButton
                            key="cancel"
                            label={t('common:cancel')}
                            onClick={closeDeleteDialog}
                            hidden={isDeletingPhoneBook || deleteDialog?.i_ua_config_directory === 0}
                        />,
                        <DialogButton
                            key="delete"
                            label={t('common:delete')}
                            onClick={handleDeleteAction}
                            loadingAnimation={isDeletingPhoneBook || deleteDialog.i_ua_config_directory === 0}
                        />,
                    ]}
                />
                
                <NewDevicePhoneBookDialog
                    isOpen={showAddNewDevicePhoneBookDialog}
                    clickOnClose={handleAddNewDevicePhoneBookClick}
                />

            </div>
        </PermissionPlaceholder>
    );
};

export default DevicePhoneBookList;
